import type * as React from "react";

/**
 * The workhorse list item: leading slot (avatar/icon) + title + optional subtitle +
 * trailing slot (chevron / badge / value). Renders as a button when `onClick` is
 * given (with a hover lift), otherwise a static row.
 */
export interface ListRowProps {
  /** Leading slot — typically an Avatar or icon. */
  leading?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Trailing slot — chevron, Badge, value, or control. */
  trailing?: React.ReactNode;
  /** When provided, the row becomes a button with a hover state. */
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
}

export function ListRow({ leading, title, subtitle, trailing, onClick, style }: ListRowProps) {
  const clickable = !!onClick;
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={onClick}
      style={
        {
          display: "flex",
          alignItems: "center",
          gap: 12,
          width: "100%",
          textAlign: "left",
          padding: "11px 13px",
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-xs)",
          cursor: clickable ? "pointer" : "default",
          fontFamily: "var(--font-sans)",
          transition: "background var(--duration-fast) var(--ease-out)",
          ...style,
        } as React.CSSProperties
      }
      onMouseEnter={
        clickable
          ? (e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.background = "var(--surface-sunken)";
            }
          : undefined
      }
      onMouseLeave={
        clickable
          ? (e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.background = "var(--surface-card)";
            }
          : undefined
      }
    >
      {leading}
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <span
          style={{
            fontSize: "var(--text-body-md)",
            fontWeight: "var(--fw-semibold)",
            color: "var(--text-strong)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </span>
        {subtitle && (
          <span
            style={{
              fontSize: "var(--text-body-sm)",
              color: "var(--text-muted)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {subtitle}
          </span>
        )}
      </span>
      {trailing}
    </Tag>
  );
}
